import type { AttemptResult, LevelDefinition } from "@/domain";
import { attemptResultSchema } from "@/domain/schemas";
import type { ActivityHostCompletionMetrics } from "@/features/activity-host";

export interface CanonicalAttemptContext {
  readonly profileId: string;
  readonly level: Pick<
    LevelDefinition,
    "activityType" | "difficulty" | "domain" | "id"
  >;
}

export interface AttemptRecordIdentity {
  // Both values are allocated once per generation and reused for every retry.
  readonly attemptId: string;
  readonly completedAt: Date;
}

export function createAttemptResult(
  context: CanonicalAttemptContext,
  metrics: ActivityHostCompletionMetrics,
  identity: AttemptRecordIdentity
): AttemptResult {
  if (!metrics.completed) {
    throw new TypeError("Only completed activities can become attempt records.");
  }

  if (metrics.levelId !== context.level.id) {
    throw new TypeError(
      'Completion level "' +
        metrics.levelId +
        '" does not match active level "' +
        context.level.id +
        '".'
    );
  }

  if (metrics.activityType !== context.level.activityType) {
    throw new TypeError(
      'Completion activity "' +
        metrics.activityType +
        '" does not match active activity "' +
        context.level.activityType +
        '".'
    );
  }

  return attemptResultSchema.parse({
    id: identity.attemptId,
    profileId: context.profileId,
    levelId: context.level.id,
    domain: context.level.domain,
    difficulty: context.level.difficulty,
    correctRate: metrics.correctRate,
    hintCount: metrics.hintCount,
    retryCount: metrics.retryCount,
    durationMs: metrics.durationMs,
    completedAt: identity.completedAt.toISOString()
  });
}
